import { useEffect, useState } from 'react';
import { cdnUrl } from '../utils/assetCache';
import { useLocale, useSettings } from '../context';
import { useSiteContent } from '../context/SiteContentContext';
import { useIsMobile } from '../hooks/useIsMobile';
import { resolveTheme } from '../utils/theme';
import { FooterSponsorsSkeleton } from './home/Skeleton';
import { helpPath } from '../data/helpData';
import FooterProviderGrid from './FooterProviderGrid';

const MOBILE_SECTION_ORDER = ['contact', 'useful', 'social', 'partners'];

const FOOTER_ICON_DEFAULTS = {
  telegram: '/footer/telegram-support.svg',
  ios: '/footer/ios.svg',
  android: '/footer/android.svg',
  age: '/footer/age-18.svg',
};

const LICENSE_SEAL_HREF =
  'https://verification.anjouangaming.eu/validate?domain=vurgunbet.com&seal_id=ae4a0c3c434a5246f379adebac8c2e4cd70fb46fd08dd2926ef07e67c8263d651dfa40e5a6127a1a1e040d6da94b02b9&stamp=52269a58d695fef7ad97815ecb325b38';
const LICENSE_SEAL_IMG = '/footer/anjouan-gaming-seal.png';

function LicenseSealLink({ className = '' }) {
  return (
    <a
      href={LICENSE_SEAL_HREF}
      className={`footer__license-seal${className ? ` ${className}` : ''}`}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Anjouan Gaming License — Verified"
      title="Anjouan Gaming License — Verified"
    >
      <img
        src={LICENSE_SEAL_IMG}
        alt="Anjouan Gaming License Verified Seal"
        width={64}
        height={64}
        loading="lazy"
      />
    </a>
  );
}

const FOOTER_SECTION_TITLE_KEYS = {
  contact: 'footer.contactUs',
  useful: 'footer.usefulInfo',
  social: 'footer.socialMedia',
  partners: 'footer.partners',
};

function parseSectionOrder(settings) {
  const raw = settings?.['footer.section_order'];
  if (!raw) return [];
  let parsed = raw;
  if (typeof raw === 'string') {
    try {
      parsed = JSON.parse(raw);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(parsed)) return [];
  return parsed.filter((id) => MOBILE_SECTION_ORDER.includes(id));
}

function localizedSetting(settings, base, locale) {
  const lang = locale === 'en' ? 'en' : 'tr';
  return settings?.[`${base}_${lang}`] || settings?.[base] || '';
}

function resolveFooterIcon(url, fallback = '') {
  const value = url || fallback;
  if (!value) return '';
  if (value.startsWith('http') || value.startsWith('/uploads') || value.startsWith('/static')) return value;
  return value.startsWith('/') ? value : `/${value}`;
}

function themedFooterIcon(url, fallback, isLight) {
  const resolved = resolveFooterIcon(url, fallback);
  if (!isLight || !resolved.startsWith('/footer/') || resolved.includes('/footer/light/')) return resolved;
  return resolved.replace('/footer/', '/footer/light/');
}

function resolveSponsorSrc(src) {
  if (!src) return '';
  if (src.startsWith('http') || src.startsWith('/static')) return cdnUrl(src);
  if (src.startsWith('/uploads/')) return cdnUrl(src);
  return src.startsWith('/') ? src : `/${src}`;
}

function parseSocialLinks(settings) {
  const raw = settings?.['footer.social_links'];
  if (Array.isArray(raw) && raw.length) {
    return raw.filter((item) => item?.href && item?.icon_url);
  }
  if (typeof raw === 'string' && raw.trim()) {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length) {
        return parsed.filter((item) => item?.href && item?.icon_url);
      }
    } catch {
      return [];
    }
  }
  return [];
}

function SocialLinksList({ links, iconSize, isLight }) {
  return links.map((item) => (
    <a
      key={`${item.label}-${item.href}`}
      href={item.href}
      className="footer__social-link"
      aria-label={item.label || 'Social'}
      target="_blank"
      rel="noopener noreferrer"
    >
      <img
        src={themedFooterIcon(item.icon_url, '', isLight)}
        alt=""
        width={iconSize}
        height={iconSize}
        loading="lazy"
      />
    </a>
  ));
}

function resolveFooterHref(item, locale) {
  if (item.pageId === 'help') return helpPath('faq', null, null, locale);
  if (item.pageId === 'vip') return '/promo-vip';
  if (item.pageId === 'basindaBiz') return '/basinda-biz';
  return item.href || '#';
}

function mapNavLinks(items, locale) {
  if (!items?.length) return [];
  return items.map((item) => ({
    label: item.label,
    href: resolveFooterHref(item, locale),
    pageId: item.pageId,
  }));
}

function cmsSlugFromHref(href) {
  if (!href || !href.startsWith('/pages/')) return null;
  return href.replace(/^\/pages\/?/, '').split('/').filter(Boolean)[0] || null;
}

function IconChevronDown({ open }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 18 18"
      fill="none"
      aria-hidden="true"
      className={`footer-mobile-section__chevron${open ? ' is-open' : ''}`}
    >
      <path
        d="M4.5 6.75 9 11.25 13.5 6.75"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconMail() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M4 6.5C4 5.67157 4.67157 5 5.5 5H18.5C19.3284 5 20 5.67157 20 6.5V17.5C20 18.3284 19.3284 19 18.5 19H5.5C4.67157 19 4 18.3284 4 17.5V6.5Z"
        stroke="currentColor"
        strokeWidth="1.5"
      />
      <path d="M5 7L12 12.5L19 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function FooterMobileSection({ sectionId, title, children, open, onToggle, flush = false }) {
  return (
    <div className={`footer-mobile-section${open ? ' is-open' : ''}`}>
      <button
        type="button"
        className="footer-mobile-section__header"
        aria-expanded={open}
        onClick={() => onToggle(sectionId)}
      >
        <span className="footer-mobile-section__caption">{title}</span>
        <span className="footer-mobile-section__toggle">
          <IconChevronDown open={open} />
        </span>
      </button>
      {open && (
        <div className={`footer-mobile-section__body${flush ? ' footer-mobile-section__body--flush' : ''}`}>
          {children}
        </div>
      )}
    </div>
  );
}

export default function Footer({ onNavigate, embedded = false }) {
  const _locale = useLocale();
  const tr = _locale.t;
  const locale = _locale.locale;
  const { settings: appSettings } = useSettings();
  const isLight = resolveTheme(appSettings.theme) === 'light';
  const isMobile = useIsMobile();
  const { nav, sponsors, settings, loading: cmsLoading } = useSiteContent();

  const linksLeft = cmsLoading ? [] : mapNavLinks(nav?.footer_left, locale);
  const linksRight = cmsLoading ? [] : mapNavLinks(nav?.footer_right, locale);
  const sponsorList = !cmsLoading && sponsors?.length ? sponsors : [];

  const email = settings?.['footer.email'] || '';
  const telegramSupport = settings?.['footer.telegram_support'] || '';
  const telegramLabel = settings?.['footer.telegram_label'] || tr('footer.telegram');
  const telegramIcon = themedFooterIcon(
    settings?.['footer.telegram_icon'],
    FOOTER_ICON_DEFAULTS.telegram,
    isLight,
  );
  const iosIcon = themedFooterIcon(settings?.['footer.icon_ios'], FOOTER_ICON_DEFAULTS.ios, isLight);
  const androidIcon = themedFooterIcon(settings?.['footer.icon_android'], FOOTER_ICON_DEFAULTS.android, isLight);
  const appCaption = localizedSetting(settings, 'footer.app_caption', locale) || tr('footer.appCaption');
  const appIosLabel = localizedSetting(settings, 'footer.app_ios_label', locale) || tr('footer.appIos');
  const appAndroidLabel = localizedSetting(settings, 'footer.app_android_label', locale) || tr('footer.appAndroid');
  const sectionTitle = (sectionId) => {
    const cmsTitle = settings?.[`footer.section_${sectionId}`]
      || localizedSetting(settings, `footer.section_${sectionId}`, locale);
    return cmsTitle || tr(FOOTER_SECTION_TITLE_KEYS[sectionId]);
  };
  const contactTitle = sectionTitle('contact');
  const usefulTitle = sectionTitle('useful');
  const socialTitle = sectionTitle('social');
  const partnersTitle = sectionTitle('partners');
  const officialPartnerLabel = tr('footer.officialPartner');
  const appIos = settings?.['footer.app_ios'] || '';
  const appAndroid = settings?.['footer.app_android'] || '';
  const socialLinks = parseSocialLinks(settings);
  const ageHref = settings?.['footer.age_href'] || '';
  const ageIcon = themedFooterIcon(settings?.['footer.age_icon'], FOOTER_ICON_DEFAULTS.age, isLight);
  const parsedSectionOrder = parseSectionOrder(settings);
  const sectionOrder = parsedSectionOrder.length ? parsedSectionOrder : MOBILE_SECTION_ORDER;
  const legalParagraphs = (() => {
    const raw = settings?.['footer.legal_paragraphs'];
    if (Array.isArray(raw) && raw.length) return raw;
    if (typeof raw === 'string' && raw.trim()) {
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed) && parsed.length) return parsed;
      } catch {
        return [raw];
      }
    }
    return [];
  })();

  const [openSection, setOpenSection] = useState(null);
  const [appTip, setAppTip] = useState(null);

  useEffect(() => {
    if (!appTip) return undefined;
    const timer = window.setTimeout(() => setAppTip(null), 2200);
    return () => window.clearTimeout(timer);
  }, [appTip]);

  const onAppClick = (event, which) => {
    event.preventDefault();
    setAppTip(which);
  };

  const toggleSection = (sectionId) => {
    setOpenSection((prev) => (prev === sectionId ? null : sectionId));
  };

  const goHelp = (e) => {
    if (!onNavigate) return;
    e.preventDefault();
    onNavigate('help');
  };

  const goVip = (e) => {
    if (!onNavigate) return;
    e.preventDefault();
    onNavigate('vip');
  };

  const goBasinda = (e) => {
    if (!onNavigate) return;
    e.preventDefault();
    onNavigate('basindaBiz');
  };

  const goCms = (e, link) => {
    if (!onNavigate) return;
    const slug = cmsSlugFromHref(link.href);
    if (!slug) return;
    e.preventDefault();
    onNavigate('cms', { cmsSlug: slug });
  };

  const handleLinkClick = (link) => (e) => {
    if (link.pageId === 'help') return goHelp(e);
    if (link.pageId === 'vip') return goVip(e);
    if (link.pageId === 'basindaBiz') return goBasinda(e);
    if (link.pageId === 'static' || link.href?.startsWith('/pages/')) return goCms(e, link);
  };

  const mobileSections = {
    contact: (
      <FooterMobileSection
        key="contact"
        sectionId="contact"
        title={contactTitle}
        open={openSection === 'contact'}
        onToggle={toggleSection}
        flush
      >
        <div className="footer-mobile-contacts">
          <div className="footer-contact-row">
            <span className="footer-contact-row__icon">
              <IconMail />
            </span>
            <div className="footer-contact-row__content">
              <a href={`mailto:${email}`} className="footer-contact-row__caption">{email}</a>
            </div>
          </div>
          <a
            href={telegramSupport}
            className="footer-contact-telegram"
            target="_blank"
            rel="noopener noreferrer"
          >
            <span className="footer-contact-telegram__icon">
              <img src={telegramIcon} alt="" width={20} height={20} loading="lazy" />
            </span>
            <span className="footer-contact-telegram__text">{telegramLabel}</span>
          </a>
        </div>
      </FooterMobileSection>
    ),
    useful: (
      <FooterMobileSection
        key="useful"
        sectionId="useful"
        title={usefulTitle}
        open={openSection === 'useful'}
        onToggle={toggleSection}
      >
        <div className="footer-mobile-links">
          {[...linksLeft, ...linksRight].map((link) => (
            <a
              key={link.label}
              href={link.href}
              className="footer__link"
              onClick={handleLinkClick(link)}
            >
              {link.label}
            </a>
          ))}
        </div>
      </FooterMobileSection>
    ),
    social: (
      <FooterMobileSection
        key="social"
        sectionId="social"
        title={socialTitle}
        open={openSection === 'social'}
        onToggle={toggleSection}
        flush
      >
        <div className="footer__socials-scroll">
          <div className="footer__socials-list">
            <SocialLinksList links={socialLinks} iconSize={44} isLight={isLight} />
          </div>
        </div>
      </FooterMobileSection>
    ),
    partners: (
      <FooterMobileSection
        key="partners"
        sectionId="partners"
        title={partnersTitle}
        open={openSection === 'partners'}
        onToggle={toggleSection}
        flush
      >
        {cmsLoading ? (
          <FooterSponsorsSkeleton />
        ) : (
          <div className="footer__partners-scroll">
            <div className="footer__partners-list">
              {sponsorList?.map((s) => (
                <a key={s.title} href={s.href || undefined} className="footer__partner-link" title={s.title}>
                  <img src={resolveSponsorSrc(s.src)} alt="" height={64} loading="lazy" />
                </a>
              ))}
            </div>
          </div>
        )}
      </FooterMobileSection>
    ),
  };

  return (
    <footer className={`footer${embedded ? ' footer--embedded' : ''}${isMobile ? ' footer--mobile' : ''}`}>
      {!embedded ? <FooterProviderGrid onNavigate={onNavigate} /> : null}
      <div className="footer__panel">
        {isMobile ? (
          <div className="footer__info">
            {sectionOrder.map((sectionId) => mobileSections[sectionId]).filter(Boolean)}
          </div>
        ) : (
          <>
        <div className="footer__links-section">
          <div className="footer__links-block">
            <div className="footer__links-col">
              {linksLeft.map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  className="footer__link"
                  onClick={handleLinkClick(link)}
                >
                  {link.label}
                </a>
              ))}
            </div>
            <div className="footer__links-col">
              {linksRight.map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  className="footer__link"
                  onClick={handleLinkClick(link)}
                >
                  {link.label}
                </a>
              ))}
            </div>
          </div>
          <div className="footer__contacts-block">
            <div className="footer__contacts">
              <a href={`mailto:${email}`} className="footer__mail">{email}</a>
            </div>
            <div className="footer__contacts-support">
              <a href={telegramSupport} className="footer__pill footer__pill--support">
                <span className="footer__icon-box">
                  <img src={telegramIcon} alt="" width={20} height={20} loading="lazy" />
                </span>
                <span className="footer__pill-text">{telegramLabel}</span>
              </a>
            </div>
          </div>
        </div>

        <div className="footer__socials-section">
          <div className="footer__apps">
            <a
              href={appIos || '#'}
              className="footer__pill footer__pill--app"
              onClick={(event) => onAppClick(event, 'ios')}
              aria-describedby={appTip === 'ios' ? 'footer-app-tip-ios' : undefined}
            >
              <span className="footer__icon-box">
                <img src={iosIcon} alt="" width={20} height={20} loading="lazy" />
              </span>
              <span className="footer__icon-text">
                <span className="footer__icon-caption">{appCaption}</span>
                <strong>{appIosLabel}</strong>
              </span>
              {appTip === 'ios' ? (
                <span className="footer-app-tip" id="footer-app-tip-ios" role="tooltip">
                  {tr('footer.appMaintenance')}
                </span>
              ) : null}
            </a>
            <a
              href={appAndroid || '#'}
              className="footer__pill footer__pill--app"
              onClick={(event) => onAppClick(event, 'android')}
              aria-describedby={appTip === 'android' ? 'footer-app-tip-android' : undefined}
            >
              <span className="footer__icon-box">
                <img src={androidIcon} alt="" width={18} height={20} loading="lazy" />
              </span>
              <span className="footer__icon-text">
                <span className="footer__icon-caption">{appCaption}</span>
                <strong>{appAndroidLabel}</strong>
              </span>
              {appTip === 'android' ? (
                <span className="footer-app-tip" id="footer-app-tip-android" role="tooltip">
                  {tr('footer.appMaintenance')}
                </span>
              ) : null}
            </a>
          </div>
          <div className="footer__socials">
            <SocialLinksList links={socialLinks} iconSize={36} isLight={isLight} />
          </div>
        </div>
          </>
        )}

        {!isMobile && (
        <div className="footer__sponsors-section">
          {cmsLoading ? (
            <FooterSponsorsSkeleton />
          ) : (
            <div className="footer__sponsors">
              {sponsorList.length ? (
                <p className="footer__sponsors-label">
                  {String(officialPartnerLabel || '')
                    .split(/\n|\r\n/)
                    .filter(Boolean)
                    .map((line) => (
                      <span key={line} className="footer__sponsors-label-line">
                        {line}
                      </span>
                    ))}
                </p>
              ) : null}
              <div className="footer__sponsors-logos">
                {sponsorList.map((s) => (
                  <a key={s.title} href={s.href || '#'} className="footer__sponsor-link" title={s.title}>
                    <img src={resolveSponsorSrc(s.src)} alt="" height={64} loading="lazy" />
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
        )}

        <div className={`footer__copyright-section${isMobile ? ' footer__copyright-section--mobile' : ''}`}>
          {isMobile ? (
            <>
              <div className="footer__copyright-mobile-top">
                <div className="footer__apps footer__apps--copyright">
                  <a
                    href={appIos || '#'}
                    className="footer-app-link"
                    onClick={(event) => onAppClick(event, 'ios')}
                    aria-describedby={appTip === 'ios' ? 'footer-app-tip-ios-m' : undefined}
                  >
                    <span className="footer-app-link__icon">
                      <img src={iosIcon} alt="" width={20} height={20} loading="lazy" />
                    </span>
                    <span className="footer-app-link__text">
                      <span className="footer-app-link__caption">{appCaption}</span>
                      <strong className="footer-app-link__platform">{appIosLabel}</strong>
                    </span>
                    {appTip === 'ios' ? (
                      <span className="footer-app-tip" id="footer-app-tip-ios-m" role="tooltip">
                        {tr('footer.appMaintenance')}
                      </span>
                    ) : null}
                  </a>
                  <a
                    href={appAndroid || '#'}
                    className="footer-app-link"
                    onClick={(event) => onAppClick(event, 'android')}
                    aria-describedby={appTip === 'android' ? 'footer-app-tip-android-m' : undefined}
                  >
                    <span className="footer-app-link__icon">
                      <img src={androidIcon} alt="" width={20} height={20} loading="lazy" />
                    </span>
                    <span className="footer-app-link__text">
                      <span className="footer-app-link__caption">{appCaption}</span>
                      <strong className="footer-app-link__platform">{appAndroidLabel}</strong>
                    </span>
                    {appTip === 'android' ? (
                      <span className="footer-app-tip" id="footer-app-tip-android-m" role="tooltip">
                        {tr('footer.appMaintenance')}
                      </span>
                    ) : null}
                  </a>
                </div>
              </div>
              <div className="footer__legal-info footer__copyright-rights">
                <div className="footer__copyright-rights-text">
                  {legalParagraphs.map((paragraph, index) => (
                    <p key={index} className="footer__legal-paragraph">{paragraph}</p>
                  ))}
                </div>
                <div className="footer__badges footer__badges--mobile">
                  <LicenseSealLink className="footer__license-seal--mobile" />
                  <a
                    href={ageHref || '#'}
                    className="footer__age-link footer__age-link--mobile"
                    aria-label="+18"
                  >
                    +18
                  </a>
                </div>
              </div>
            </>
          ) : (
            <div className="footer__copyright-row">
              <div className="footer__badges">
                <a href={ageHref} className="footer__age-link" aria-label="+18">
                  <img src={ageIcon} alt="+18" width={28} height={28} loading="lazy" />
                </a>
                <LicenseSealLink />
              </div>
              <div className="footer__legal-info footer__legal-info--desktop">
                {legalParagraphs.map((paragraph, index) => (
                  <p key={index} className="footer__legal-paragraph">{paragraph}</p>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </footer>
  );
}
